import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { getArticles, deleteArticle } from '../services/articleService';
import ExplorerHeader from '../components/explorer/ExplorerHeader';
import PageFooter from '../components/PageFooter';
import './ExplorerPage.css';
import './ArticlesPage.css';

const PAGE_SIZE = 9;

const ArticlesPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const s = t.articles;

  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState('title');

  const isAdmin = currentUser?.role === 'ADMIN';

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    getArticles(
      { page: currentPage, pageSize: PAGE_SIZE, search: searchQuery, searchScope, lang },
      controller.signal
    )
      .then((result) => {
        if (!isMounted) return;
        setArticles(result.articles);
        setTotalPages(result.totalPages);
      })
      .catch((err) => {
        if (!isMounted || err.name === 'AbortError') return;
        setError(s.errorLoad);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [currentPage, searchQuery, searchScope, lang, s.errorLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScopeChange = (scope) => {
    setSearchScope(scope);
    setCurrentPage(1);
  };

  const handleArticleClick = (article) => {
    const id = article._id || article.id;
    navigate(lang === 'he' ? `/heb/articles/${id}` : `/articles/${id}`, { state: { article } });
  };

  const handleDelete = async (e, article) => {
    e.stopPropagation();
    if (!window.confirm(s.confirmDelete)) return;
    try {
      await deleteArticle(article._id || article.id);
      setArticles((prev) => prev.filter((a) => (a._id || a.id) !== (article._id || article.id)));
    } catch {
      alert(s.errorDelete);
    }
  };

  const handleEdit = (e, article) => {
    e.stopPropagation();
    const id = article._id || article.id;
    navigate(lang === 'he' ? `/heb/admin/articles/${id}/edit` : `/admin/articles/${id}/edit`);
  };

  const handleAdd = () => {
    navigate(lang === 'he' ? '/heb/admin/articles/new' : '/admin/articles/new');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(lang === 'he' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="explorer-page">
      <main className="explorer-main">
        <div className="explorer-header-row">
          <ExplorerHeader
            eyebrow={s.eyebrow}
            title={s.title}
            accent={s.accent}
            description={s.description}
          />
          {isAdmin && (
            <button type="button" className="explorer-add-btn" onClick={handleAdd}>
              {s.addArticle}
            </button>
          )}
        </div>

        <div className="explorer-search-bar" dir={lang === 'he' ? 'rtl' : undefined}>
          <span className="material-symbols-rounded explorer-search-icon" aria-hidden="true">search</span>
          <input
            className="explorer-search-input"
            type="search"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            placeholder={s.searchPlaceholder}
            aria-label={s.searchPlaceholder}
          />
          <div className="explorer-search-scope">
            <button
              type="button"
              className={`explorer-scope-btn${searchScope === 'title' ? ' is-active' : ''}`}
              onClick={() => handleScopeChange('title')}
            >
              {s.searchScopeTitle}
            </button>
            <button
              type="button"
              className={`explorer-scope-btn${searchScope === 'content' ? ' is-active' : ''}`}
              onClick={() => handleScopeChange('content')}
            >
              {s.searchScopeContent}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="recipe-empty-state"><p>{s.loading}</p></div>
        ) : error ? (
          <div className="recipe-empty-state"><p style={{ color: '#c0392b' }}>{error}</p></div>
        ) : articles.length === 0 ? (
          <div className="recipe-empty-state"><p>{searchQuery ? s.notFound : s.noArticles}</p></div>
        ) : (
          <div className="articles-grid">
            {articles.map((article) => {
              const id = article._id || article.id;
              return (
                <article
                  key={id}
                  className="article-card"
                  onClick={() => handleArticleClick(article)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleArticleClick(article)}
                  role="button"
                  aria-label={article.title}
                >
                  {article.imageUrl && (
                    <div className="article-card-image-wrap">
                      <img src={article.imageUrl} alt={article.title} className="article-card-image" loading="lazy" />
                    </div>
                  )}
                  <div className="article-card-body">
                    {article.tags?.length > 0 && (
                      <div className="article-card-tags">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="article-card-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <h2 className="article-card-title">{article.title}</h2>
                    {article.summary && <p className="article-card-summary">{article.summary}</p>}
                    <div className="article-card-meta">
                      <span className="article-card-date">{formatDate(article.createdAt)}</span>
                      {article.authorName && (
                        <span className="article-card-author">{s.by} {article.authorName}</span>
                      )}
                    </div>
                    <div className="article-card-footer">
                      <span className="article-card-read-more">{s.readMore} ?</span>
                      {isAdmin && (
                        <div className="article-card-admin-actions">
                          <button className="article-admin-btn article-admin-btn--edit" onClick={(e) => handleEdit(e, article)} title={s.editArticle} aria-label={s.editArticle}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button className="article-admin-btn article-admin-btn--delete" onClick={(e) => handleDelete(e, article)} title={s.deleteArticle} aria-label={s.deleteArticle}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="articles-pagination">
            <button className="articles-page-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>�</button>
            <span className="articles-page-info">{currentPage} / {totalPages}</span>
            <button className="articles-page-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>�</button>
          </div>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default ArticlesPage;
